import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { LoadError } from '@/components/campaigns/LoadError'
import { formatNumber } from '@/components/campaigns/campaignFormat'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Label } from '@/components/ui/label'
import { SeccionPlegable } from '@/components/ui/seccion-plegable'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { useCampaignSettingsQuery, useConsentSummaryQuery, useUpdateCampaignSettings } from '@/queries'
import type { CampaignSettings, ConsentMode } from '@/services/crmCampaignsService'

const SUMMARY_DAYS = 30

// Las automatizaciones de `/actions` a las que se puede aplicar. Quedan fuera las
// que nunca se consultan (R11.10): la petición de consentimiento, los códigos de
// acceso, los avisos a empleados y las pruebas. Las campañas lo comprueban aparte.
const AUTOMATIONS = [
  'pre_meeting',
  'post_meeting',
  'match_outreach',
  'betterplace_coordination_followup',
  'rising_offer_reminder',
  'post_call_followup',
  'search_alert_match',
  'price_drop_digest',
  'price_drop',
  'rising_offer_received',
  'rising_offer_updated',
  'rising_offer_winner',
  'rising_offer_not_winner',
  'buyer_reactivation_check',
]
const EXEMPT = new Set(['first_contact', 'auth_otp', 'agent_notice', 'post_call_template_test', 'crm_campaign'])
// Con `aplicar`, un error de consulta también deja de enviar: cuenta como descartado.
const DISCARDING_RESULTS = ['no_aceptada', 'no_cubierto', 'sin_contacto', 'error']

type Pending =
  | { kind: 'mode'; sendType: string; discarded: number }
  | { kind: 'write_to_crm' }

/**
 * Qué hace cada automatización con quien no ha aceptado: `observar` solo apunta a
 * quién habría dejado fuera, y `aplicar` deja de enviarle.
 *
 * Pasar a `aplicar` se confirma con la cifra delante. Es lo que permite decidir
 * con datos, y lo que evita descubrir después que un recordatorio dejó de salir.
 */
export function ConsentModesCard() {
  const { t } = useTranslation()
  return (
    <SeccionPlegable
      numero="10"
      etiqueta={t('consent_modes.label')}
      titulo={t('consent_modes.title')}
      descripcion={t('consent_modes.description')}
    >
      <ConsentModesContent />
    </SeccionPlegable>
  )
}

function ConsentModesContent() {
  const { t } = useTranslation()
  const { lang = 'es' } = useParams()
  const settings = useCampaignSettingsQuery()
  const summary = useConsentSummaryQuery(SUMMARY_DAYS)
  const update = useUpdateCampaignSettings()
  const [pending, setPending] = useState<Pending | null>(null)

  const sendTypes = useMemo(() => {
    const known = new Set(AUTOMATIONS)
    for (const key of Object.keys(settings.data?.rgpd_modos ?? {})) known.add(key)
    for (const key of Object.keys(summary.data?.por_send_type ?? {})) known.add(key)
    return [...known].filter((key) => !EXEMPT.has(key))
  }, [settings.data, summary.data])

  const discardedFor = (sendType: string) => {
    const results = summary.data?.por_send_type?.[sendType] ?? {}
    return DISCARDING_RESULTS.reduce((total, result) => total + (results[result] ?? 0), 0)
  }

  const save = (current: CampaignSettings, patch: Partial<CampaignSettings>, doneMessage: string) => {
    update.mutate(
      { ...current, ...patch },
      {
        onSuccess: () => toast.success(doneMessage),
        onError: (error) => toast.error(error.message),
        onSettled: () => setPending(null),
      },
    )
  }

  if (settings.isPending) {
    return (
      <div className="space-y-2" data-testid="consent-modes-loading">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }
  if (settings.isError) return <LoadError error={settings.error} onRetry={() => void settings.refetch()} />

  const current = settings.data

  const applyMode = (sendType: string, mode: ConsentMode) =>
    save(
      current,
      { rgpd_modos: { ...current.rgpd_modos, [sendType]: mode } },
      t('consent_modes.mode_saved', { name: automationName(sendType), mode: t(`consent_modes.mode.${mode}`) }),
    )

  function automationName(sendType: string) {
    return t(`consent_modes.send_type.${sendType}`, { defaultValue: sendType })
  }

  const changeMode = (sendType: string, mode: ConsentMode) => {
    if (mode === 'aplicar') setPending({ kind: 'mode', sendType, discarded: discardedFor(sendType) })
    else applyMode(sendType, mode)
  }

  const confirm = () => {
    if (pending?.kind === 'mode') applyMode(pending.sendType, 'aplicar')
    else if (pending?.kind === 'write_to_crm') {
      save(current, { escribir_consentimiento_crm: true }, t('consent_modes.write_on'))
    }
  }

  return (
    <div className="min-w-0 space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Label htmlFor="consent-write-crm">{t('consent_modes.write_label')}</Label>
          <p className="text-xs text-muted-foreground">{t('consent_modes.write_help')}</p>
        </div>
        <Switch
          id="consent-write-crm"
          aria-label={t('consent_modes.write_label')}
          checked={current.escribir_consentimiento_crm}
          disabled={update.isPending}
          onCheckedChange={(checked) => {
            if (checked) setPending({ kind: 'write_to_crm' })
            else save(current, { escribir_consentimiento_crm: false }, t('consent_modes.write_off'))
          }}
        />
      </div>

      {summary.isError ? <LoadError error={summary.error} onRetry={() => void summary.refetch()} /> : null}

      <ul className="divide-y">
        {sendTypes.map((sendType) => {
          const selectId = `consent-mode-${sendType}`
          const known = summary.isSuccess
          return (
            <li key={sendType} className="flex flex-wrap items-center justify-between gap-4 py-2">
              <div className="min-w-0 space-y-1">
                <Label htmlFor={selectId}>{automationName(sendType)}</Label>
                <p className="text-xs text-muted-foreground" data-testid={`discarded-${sendType}`}>
                  {known
                    ? t('consent_modes.would_discard', {
                        count: formatNumber(discardedFor(sendType), lang),
                        days: SUMMARY_DAYS,
                      })
                    : t('consent_modes.no_figure')}
                </p>
              </div>
              <Select
                value={current.rgpd_modos[sendType] ?? 'observar'}
                disabled={update.isPending}
                onValueChange={(mode) => changeMode(sendType, mode as ConsentMode)}
              >
                <SelectTrigger id={selectId} className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="observar">{t('consent_modes.mode.observar')}</SelectItem>
                  <SelectItem value="aplicar">{t('consent_modes.mode.aplicar')}</SelectItem>
                </SelectContent>
              </Select>
            </li>
          )
        })}
      </ul>

      <AlertDialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending?.kind === 'write_to_crm'
                ? t('consent_modes.confirm_write_title')
                : t('consent_modes.confirm_apply_title', {
                    name: pending?.kind === 'mode' ? automationName(pending.sendType) : '',
                  })}
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="consent-confirm-text">
              {pending?.kind === 'write_to_crm'
                ? t('consent_modes.confirm_write_description')
                : t('consent_modes.confirm_apply_description', {
                    count: formatNumber(pending?.kind === 'mode' ? pending.discarded : 0, lang),
                    days: SUMMARY_DAYS,
                  })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('campaigns.common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={update.isPending} onClick={confirm}>
              {t('consent_modes.confirm_action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
